/**
 * Mirror combo filename parser (browser). Keep in sync with scripts/mirror-combo-parser.mjs
 */
(function (global) {
  'use strict';

  var DISTRESS_WORDS = { heavy: 1, distress: 1, distressed: 1, heavydistress: 1 };
  var DECORATIVE_SUFFIX_WORDS = { leaf: 1, lead: 1, frame: 1 };
  var KNOWN_COLORS = { gold: 1, copper: 1, silver: 1, charcoal: 1, pink: 1, chameleon: 1 };

  function filenameFromUrl(filename) {
    if (!filename) return '';
    try {
      var path = new URL(filename).pathname;
      var base = path.split('/').pop() || '';
      return base.replace(/\?.*$/, '');
    } catch (e) {
      var parts = String(filename).split('/');
      return (parts.pop() || '').split('?')[0];
    }
  }

  function normalizeTokens(value) {
    return String(value || '')
      .replace(/\.[a-z0-9]+$/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .split(' ')
      .filter(Boolean);
  }

  function parseColorFromFirstToken(firstToken) {
    if (!firstToken) return { color: null };
    var token = firstToken.toLowerCase();
    if (token === 'blue' || token === 'blueheavy' || token.indexOf('blue') === 0) return { color: 'ocean' };
    if (KNOWN_COLORS[token]) return { color: token };
    if (token.slice(-5) === 'heavy') {
      var base = token.slice(0, -5);
      if (base === 'blue') return { color: 'ocean' };
      if (KNOWN_COLORS[base]) return { color: base };
    }
    return { color: null };
  }

  function stripDistressTokens(tokens) {
    return tokens.filter(function (token) {
      return !DISTRESS_WORDS[token];
    });
  }

  function findShape(tokens) {
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      if (t === 'round') return { shape: 'round', shapeIndex: i };
      if (t === 'rectangular' || t === 'rectangle') return { shape: 'rectangle', shapeIndex: i };
    }
    return { shape: null, shapeIndex: -1 };
  }

  function normalizeFrameToken(token) {
    if (!token) return null;
    if (token === 'iron') return 'iron';
    if (token === 'gold') return 'gold';
    if (token === 'silver') return 'silver';
    if (token === 'floating') return 'floating';
    if (token === 'frameless') return 'frameless';
    return null;
  }

  function findFrame(tokens, shapeIndex) {
    if (shapeIndex < 0) {
      return { frame: null, reason: 'missing frame token after shape' };
    }

    var afterShape = tokens.slice(shapeIndex + 1);
    var candidates = [];
    for (var i = 0; i < afterShape.length; i++) {
      var token = afterShape[i];
      if (DECORATIVE_SUFFIX_WORDS[token]) continue;
      var normalized = normalizeFrameToken(token);
      if (!normalized) continue;
      if (normalized === 'frameless') {
        return {
          frame: null,
          reason: "missing shape token, frame is 'frameless' — needs manual review",
        };
      }
      candidates.push(normalized);
    }

    if (candidates.length) {
      return { frame: candidates[candidates.length - 1] };
    }

    if (shapeIndex > 0 && tokens[shapeIndex - 1] === 'float') {
      return { frame: 'floating' };
    }

    return { frame: null, reason: 'missing frame token after shape' };
  }

  function parseMirrorFilename(filename) {
    var baseTokens = normalizeTokens(filename);
    if (!baseTokens.length) {
      return {
        status: 'needsReview',
        key: null,
        color: null,
        shape: null,
        frame: null,
        reason: 'empty filename',
        normalizedTokens: [],
      };
    }

    var colorResult = parseColorFromFirstToken(baseTokens[0]);
    var color = colorResult.color;
    if (!color) {
      return {
        status: 'needsReview',
        key: null,
        color: null,
        shape: null,
        frame: null,
        reason: "unknown color token '" + baseTokens[0] + "'",
        normalizedTokens: baseTokens,
      };
    }

    var hasFrameless = baseTokens.indexOf('frameless') !== -1;
    var workingTokens = stripDistressTokens(baseTokens.slice(1));

    var shapeResult = findShape(workingTokens);
    var shape = shapeResult.shape;
    var shapeIndex = shapeResult.shapeIndex;
    if (!shape) {
      return {
        status: 'needsReview',
        key: null,
        color: color,
        shape: null,
        frame: null,
        reason: hasFrameless
          ? "missing shape token, frame is 'frameless' — needs manual review"
          : 'missing shape token (round/rectangular/rectangle)',
        normalizedTokens: workingTokens,
      };
    }

    var hasFrameAfterShape = workingTokens.slice(shapeIndex + 1).some(function (token) {
      return token === 'iron' || token === 'gold' || token === 'silver' || token === 'floating' || token === 'frameless';
    });

    if (shapeIndex > 0 && workingTokens[shapeIndex - 1] === 'float' && hasFrameAfterShape) {
      workingTokens = workingTokens.filter(function (_, idx) {
        return idx !== shapeIndex - 1;
      });
    }

    var shapeRef = findShape(workingTokens);
    var frameResult = findFrame(workingTokens, shapeRef.shapeIndex);
    if (!frameResult.frame) {
      return {
        status: 'needsReview',
        key: null,
        color: color,
        shape: shape,
        frame: null,
        reason: frameResult.reason || 'frame not resolved',
        normalizedTokens: workingTokens,
      };
    }

    var key = shape + '|' + color + '|' + frameResult.frame;
    return {
      status: 'parsed',
      key: key,
      color: color,
      shape: shape,
      frame: frameResult.frame,
      reason: null,
      normalizedTokens: workingTokens,
    };
  }

  global.MirrorComboParser = {
    filenameFromUrl: filenameFromUrl,
    parseMirrorFilename: parseMirrorFilename,
  };
})(typeof window !== 'undefined' ? window : globalThis);
